<template>
  <div class="upload-drag-and-drop">
    <div class="upload-drag-and-drop__wrapper">
      <div class="upload-drag-and-drop__entries">
        <upload-preview
          v-if="uploadedFiles.length > 0"
          :files="uploadedFiles"
          @remove="removeUploadedFile"
        />
        <removable-entry
          v-if="errorMessage"
          bold
          color="red"
          @close="hideErrorMessage"
        >
          {{ errorMessage }}
        </removable-entry>
      </div>
      <div class="upload-drag-and-drop__file-upload">
        <loading-indicator v-if="loading" />
        <file-upload
          ref="upload"
          v-model="files"
          class="trbo-a-file-upload__uploader"
          input-id="file-upload"
          accept="image/png,image/gif,image/jpeg,image/webp,application/pdf"
          :multiple="true"
          :drop="true"
          :size="maxFileSizeInBytes"
          :custom-action="uploadFile"
          @input-file="inputFile"
          @input-filter="inputFilter"
        >
          <div :class="classes">
            <img src="/upload.svg" class="upload-drag-and-drop__file-upload__icon" alt="">
            <div :id="uploadLabelId" class="upload-drag-and-drop__file-upload__text">{{ $t('wizard.clickToAddFiles') }}</div>
          </div>
        </file-upload>
      </div>
    </div>
  </div>
</template>

<script>
// @flow

import VueUploadComponent from 'vue-upload-component'

import { deleteFileFromS3, uploadFileToS3 } from '../../../../common/awsS3'

import LoadingIndicator from '../../../../common/components/LoadingIndicator/LoadingIndicator.vue'

import RemovableEntry from './RemovableEntry.vue'
import UploadPreview from './UploadPreview.vue'

export default {
  components: {
    FileUpload: VueUploadComponent,
    LoadingIndicator,
    UploadPreview,
    RemovableEntry
  },
  props: {
    message: {
      type: Object,
      default: () => {
      }
    }
  },
  data () {
    return {
      uploadedFiles: [],
      files: [],
      loading: false,
      errorMessage: null,
      maxFileSizeInBytes: 10485760, // 10MB in bytes
      isFocused: false
    }
  },
  computed: {
    classes () {
      return {
        'upload-drag-and-drop__file-upload__wrapper': true,
        'upload-drag-and-drop__file-upload__wrapper--is-focused': this.isFocused
      }
    },
    uploadLabelId () {
      return `upload-label-${this.message.id}`
    }
  },
  mounted () {
    this.$refs.upload?.$children[0]?.$el.addEventListener('focus', this.handleFocus)
    this.$refs.upload?.$children[0]?.$el.addEventListener('blur', this.handleBlur)
    this.$refs.upload?.$el.querySelector('label')?.setAttribute('aria-labelledby', this.uploadLabelId)
    this.$refs.upload?.$el.querySelector('input')?.setAttribute('aria-labelledby', this.uploadLabelId)
  },
  beforeDestroy () {
    this.$refs.upload?.$children[0]?.$el.removeEventListener('focus', this.handleFocus)
    this.$refs.upload?.$children[0]?.$el.removeEventListener('blur', this.handleBlur)
  },
  methods: {
    inputFilter (newFile, oldFile, prevent) {
      this.errorMessage = null
      // Add file
      if (newFile && !oldFile) {
        if (!/\.(jpeg|jpe|jpg|gif|png|webp|pdf)$/i.test(newFile.name)) {
          this.errorMessage = 'We do not support this file format. Please upload a gif, jpg, jpeg, png, webp, or pdf file.'
          return prevent()
        }

        if (newFile.size > this.maxFileSizeInBytes) {
          this.errorMessage = "The file you want to upload is too large. Please make sure it's below 10 MB and try again."
          return prevent()
        }
      }
    },
    async inputFile (newFile, oldFile) {
      // Add new file
      if (newFile && !oldFile) {
        this.loading = true
      }

      // Update file
      if (newFile && oldFile) {
        // Upload error
        if (newFile.error !== oldFile.error) {
          this.loading = false
        }

        // Uploaded successfully
        if (newFile.success !== oldFile.success) {
          this.loading = false
        }
      }

      if (!newFile && oldFile) {
        // Remove file
        this.loading = true

        if (oldFile.success && oldFile.key) {
          this.uploadedFiles = this.uploadedFiles.filter(file => !file.url.includes(oldFile.key))
          this.$emit('submit', this.uploadedFiles)
          await deleteFileFromS3(this.$store.state.customerId, this.$store.state.subscriberId, oldFile.key)
        }

        this.loading = false
      }

      // Auto upload
      if (Boolean(newFile) !== Boolean(oldFile) || oldFile.error !== newFile.error) {
        if (!this.$refs.upload.active) {
          this.$refs.upload.active = true
        }
      }
    },
    async uploadFile (file) {
      this.loading = true
      const uploadedFile = await uploadFileToS3(this.$store.state.customerId, this.$store.state.subscriberId, file.file)
      this.loading = false

      if (uploadedFile) {
        await this.$refs.upload.update(file, { key: uploadedFile.key })
        this.uploadedFiles.push({
          name: file.file.name,
          url: uploadedFile.fullPath,
          uploadedFileKey: uploadedFile.key
        })
        this.$emit('submit', this.uploadedFiles)
      } else {
        this.errorMessage = 'Unexpected error when saving the file. Please try again to upload the same file.'
      }
    },
    removeUploadedFile (uploadFileKey) {
      this.uploadedFiles = this.uploadedFiles.filter(file => file.uploadedFileKey !== uploadFileKey)
      this.files = this.files.filter(file => file.key !== uploadFileKey)
      this.$emit('submit', this.uploadedFiles)
    },
    hideErrorMessage () {
      this.errorMessage = null
    },
    handleFocus () {
      this.isFocused = true
    },
    handleBlur () {
      this.isFocused = false
    }
  }
}
</script>

<style src="./upload-drag-and-drop.scss" />
